import React from "react";
import {
  ConnectorLogo,
  ConnectorConnectionStatus,
  StatusPill,
  type StatusVariant,
} from "../../atoms";
import {
  Actions,
  Container,
  HeaderButton,
  Identity,
  Name,
  NameGroup,
} from "./ConnectionDetailHeader.styles";
import type { ConnectionDetailHeaderProps } from "./ConnectionDetailHeader.types";
import { ReconnectIcon } from "./ReconnectIcon";

const ICON_SIZE = 24;

const RefreshIcon = () => (
  <img src="/dataconnector/refresh-cw.svg" width={ICON_SIZE} height={ICON_SIZE} aria-hidden alt="" />
);
const DisconnectIcon = () => (
  <img src="/dataconnector/x-circle.svg" width={ICON_SIZE} height={ICON_SIZE} aria-hidden alt="" />
);
const DeleteIcon = () => (
  <img src="/dataconnector/trash-2.svg" width={ICON_SIZE} height={ICON_SIZE} aria-hidden alt="" />
);

const statusToPill = (
  status: ConnectorConnectionStatus,
): { variant: StatusVariant; label: string } => {
  switch (status) {
    case "connected":
      return { variant: "success", label: "Connected" };
    case "disconnected":
      return { variant: "error", label: "Disconnected" };
    case "error":
      return { variant: "error", label: "Error" };
    case "not_connected":
    default:
      return { variant: "neutral", label: "Not connected" };
  }
};

/**
 * Top-of-page header for the Connection Detail (UI-4): connector logo +
 * instance name + status pill on the left, action strip on the right.
 *
 * Stateless — all action handlers are owned by the parent so
 * confirmation modals (Disconnect / Delete) can be wired up at the page
 * level without coupling them to this presentational component.
 */
export const ConnectionDetailHeader: React.FC<ConnectionDetailHeaderProps> = ({
  iconUrl,
  instanceName,
  status,
  onRefresh,
  onDisconnect,
  onDelete,
  isActionDisabled = false,
  chromeless = false,
  showRefresh = true,
  className,
}) => {
  const pill = statusToPill(status);
  const isDisconnected = status === "disconnected";
  const toggleButtonLabel = isDisconnected ? "Reconnect" : "Disconnect";
  const toggleButtonIcon = isDisconnected ? (
    <ReconnectIcon size={ICON_SIZE} />
  ) : (
    <DisconnectIcon />
  );

  return (
    <Container className={className} $chromeless={chromeless}>
      <Identity>
        <ConnectorLogo src={iconUrl} name={instanceName} size="md" />
        <NameGroup>
          <Name>{instanceName}</Name>
          <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
        </NameGroup>
      </Identity>
      <Actions>
        {showRefresh ? (
          <HeaderButton
            variant="secondary"
            size="md"
            onClick={onRefresh}
            disabled={isActionDisabled || isDisconnected}
            aria-label="Refresh connection"
          >
            <RefreshIcon />
          </HeaderButton>
        ) : null}
        <HeaderButton
          variant="secondary"
          size="md"
          iconBefore={toggleButtonIcon}
          onClick={onDisconnect}
          disabled={isActionDisabled}
          aria-label={toggleButtonLabel}
        >
          {toggleButtonLabel}
        </HeaderButton>
        <HeaderButton
          variant="danger-outline"
          size="md"
          iconBefore={<DeleteIcon />}
          onClick={onDelete}
          disabled={isActionDisabled}
        >
          Delete
        </HeaderButton>
      </Actions>
    </Container>
  );
};

export default ConnectionDetailHeader;
